import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, CheckCircle, AlertCircle, Shield } from 'lucide-react';
import { authService } from '../../services/authService';
import MedicalAnimation from '../../components/MedicalAnimation';

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!email?.trim()) {
      setError('Please enter your email address.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email?.trim())) {
      setError('Please enter a valid email address.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { error: resetError } = await authService.resetPassword(email);

      if (resetError) {
        if (resetError?.message?.includes('user-not-found') || resetError?.message?.includes('not found')) {
          setError('No account found with this email address. Please check and try again.');
        } else if (resetError?.message?.includes('too-many-requests')) {
          setError('Too many reset attempts. Please wait a few minutes before trying again.');
        } else if (resetError?.message?.includes('invalid-email')) {
          setError('Invalid email address format. Please check and try again.');
        } else {
          setError(resetError?.message || 'Failed to send reset email. Please try again.');
        }
      } else {
        setSuccess(true);
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  const handleResend = () => {
    setSuccess(false);
    setError('');
    handleSubmit({ preventDefault: () => {} });
  };

  return (
    <div className="min-h-screen relative overflow-hidden">
      {/* Background Animation */}
      <div className="absolute inset-0">
        <MedicalAnimation />
      </div>

      {/* Main Content */}
      <div className="relative z-10 min-h-screen flex items-center justify-center p-4">
        <div
          className="w-full max-w-md rounded-2xl shadow-2xl overflow-hidden"
          style={{
            background: 'rgba(10, 20, 40, 0.85)',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(0, 229, 255, 0.2)',
            boxShadow: '0 0 60px rgba(0, 229, 255, 0.15), 0 25px 50px -12px rgba(0, 0, 0, 0.5)',
          }}
        >
          <div className="p-6">
            {/* Back to Login Link */}
            <Link
              to="/login"
              className="inline-flex items-center gap-1.5 text-xs text-cyan-400/70 hover:text-cyan-300 transition-colors mb-4"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to Login
            </Link>

            {/* Header */}
            <div className="text-center mb-5">
              <div
                className="inline-flex items-center justify-center w-14 h-14 rounded-full mb-3"
                style={{
                  background: 'rgba(0, 229, 255, 0.1)',
                  border: '1px solid rgba(0, 229, 255, 0.25)',
                }}
              >
                <Shield className="h-7 w-7 text-cyan-400" />
              </div>
              <h1 className="text-xl font-bold text-white mb-1">Forgot Password?</h1>
              <p className="text-cyan-300/60 text-xs leading-relaxed">
                Enter your email address and we'll send you a secure link to reset your password.
              </p>
            </div>

            {!success ? (
              /* Step 1: Email Input Form */
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* 2FA Info Banner */}
                <div
                  className="flex items-start gap-2.5 p-3 rounded-lg"
                  style={{
                    background: 'rgba(0, 229, 255, 0.06)',
                    border: '1px solid rgba(0, 229, 255, 0.12)',
                  }}
                >
                  <Mail className="h-4 w-4 text-cyan-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-cyan-300/70 leading-relaxed">
                    <span className="font-medium text-cyan-300/90">Email verification (2FA): </span>
                    A secure password reset link will be sent to your registered email for identity verification.
                  </div>
                </div>

                {/* Email Field */}
                <div>
                  <label htmlFor="reset-email" className="block text-xs font-medium text-cyan-200/80 mb-1">
                    Email Address
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-4 w-4 text-cyan-400/60" />
                    </div>
                    <input
                      id="reset-email"
                      type="email"
                      value={email}
                      onChange={(e) => {
                        setEmail(e?.target?.value);
                        if (error) setError('');
                      }}
                      className="block w-full pl-9 pr-3 py-2.5 border border-cyan-500/30 rounded-lg bg-slate-900/50 text-white placeholder-slate-400 text-sm focus:outline-none focus:ring-2 focus:ring-cyan-500/50 focus:border-cyan-500/50 transition-all"
                      placeholder="Enter your registered email"
                      disabled={loading}
                      autoFocus
                    />
                  </div>
                </div>

                {/* Error Message */}
                {error && (
                  <div className="flex items-start gap-2 bg-red-500/15 border border-red-500/30 rounded-lg p-2.5">
                    <AlertCircle className="h-4 w-4 text-red-400 mt-0.5 flex-shrink-0" />
                    <p className="text-xs text-red-300">{error}</p>
                  </div>
                )}

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={loading || !email?.trim()}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-transparent rounded-lg text-sm font-semibold text-slate-900 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    background: 'linear-gradient(135deg, #00e5ff 0%, #00bcd4 100%)',
                    boxShadow: loading ? 'none' : '0 0 20px rgba(0, 229, 255, 0.4)',
                  }}
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-slate-900 mr-2"></div>
                      Sending Reset Link...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Send Password Reset Link
                    </>
                  )}
                </button>
              </form>
            ) : (
              /* Step 2: Success Confirmation */
              <div className="space-y-4">
                {/* Success Icon & Message */}
                <div
                  className="text-center p-5 rounded-xl"
                  style={{
                    background: 'rgba(16, 185, 129, 0.08)',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <div
                    className="inline-flex items-center justify-center w-12 h-12 rounded-full mb-3"
                    style={{
                      background: 'rgba(16, 185, 129, 0.15)',
                    }}
                  >
                    <CheckCircle className="h-6 w-6 text-emerald-400" />
                  </div>
                  <h2 className="text-base font-semibold text-white mb-2">Reset Link Sent!</h2>
                  <p className="text-xs text-emerald-300/70 leading-relaxed">
                    A password reset link has been sent to{' '}
                    <span className="font-medium text-emerald-300">{email}</span>.
                    Please check your inbox and spam folder.
                  </p>
                </div>

                {/* Instructions */}
                <div
                  className="p-3 rounded-lg space-y-2"
                  style={{
                    background: 'rgba(0, 229, 255, 0.04)',
                    border: '1px solid rgba(0, 229, 255, 0.1)',
                  }}
                >
                  <h3 className="text-xs font-semibold text-cyan-200/90">Next Steps:</h3>
                  <ol className="text-xs text-cyan-300/60 space-y-1.5 list-decimal list-inside">
                    <li>Open the email from KMC Hospital</li>
                    <li>Click the secure reset link</li>
                    <li>Create your new password</li>
                    <li>Sign in with your new password</li>
                  </ol>
                </div>

                {/* Action Buttons */}
                <div className="space-y-2">
                  <button
                    onClick={handleResend}
                    disabled={loading}
                    className="w-full py-2 px-4 rounded-lg text-sm font-medium text-cyan-300 transition-all hover:bg-cyan-500/10 disabled:opacity-50"
                    style={{
                      border: '1px solid rgba(0, 229, 255, 0.25)',
                    }}
                  >
                    {loading ? 'Sending...' : 'Resend Reset Link'}
                  </button>

                  <Link
                    to="/login"
                    className="w-full flex justify-center items-center py-2.5 px-4 rounded-lg text-sm font-semibold text-slate-900 transition-all"
                    style={{
                      background: 'linear-gradient(135deg, #00e5ff 0%, #00bcd4 100%)',
                      boxShadow: '0 0 20px rgba(0, 229, 255, 0.4)',
                    }}
                  >
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    Back to Login
                  </Link>
                </div>
              </div>
            )}

            {/* Footer */}
            <div className="mt-4 text-center">
              <p className="text-xs text-cyan-300/40">
                Remember your password?{' '}
                <Link to="/login" className="text-cyan-400/70 hover:text-cyan-300 transition-colors font-medium">
                  Sign In
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
